import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { storeQuestionBankQuestionTime } from "../services/questionBankQuestionService";
import { getGamificationBattleClient } from "../homeDashboard/gamification/gamificationBattleClient";
import {
  buildGamificationFlowState,
  persistGamificationFlowState,
} from "../homeDashboard/gamification/gamificationFlowState";

function getRequiredPlayers(flowState) {
  const battleId = Number(flowState?.battles_id || flowState?.selectedBattle?.battles_id || 0);
  if (battleId === 1) {
    if (flowState?.entryMode === "friend") return 3;
    return Math.max(2, Number(flowState?.group_user_limit || 0));
  }
  if (battleId === 2) {
    return 2;
  }
  return 0;
}

export function useGamificationBattleSession(routeState = {}) {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const client = useMemo(() => getGamificationBattleClient(), []);
  const authenticatedUserId = Number(user?.user_id || user?.id || 0);
  const normalizedRouteState = useMemo(
    () => buildGamificationFlowState(routeState),
    [routeState]
  );
  const routeStateRef = useRef(normalizedRouteState);
  const lastSyncedRouteStateRef = useRef(null);
  const [state, setState] = useState(() =>
    buildGamificationFlowState(normalizedRouteState, client.state)
  );
  const [questionTimeLeft, setQuestionTimeLeft] = useState(0);
  const [questionTimeTaken, setQuestionTimeTaken] = useState(0);
  const [startCountdownLeft, setStartCountdownLeft] = useState(0);
  const [waitingCountdownLeft, setWaitingCountdownLeft] = useState(0);
  const handledAnswerRef = useRef("");
  const questionDeadlineRef = useRef(0);
  const autoSearchTriggeredRef = useRef(false);
  const autoQuestionCompleteRef = useRef("");
  const answerScoreRefreshRef = useRef("");
  const autoPingTriggeredRef = useRef(false);
  const autoRandomStartRef = useRef("");
  const waitingStartedRef = useRef(false);
  const waitingDeadlineRef = useRef(0);
  const confirmationResolvedRef = useRef(0);
  const autoRandomStartTimerRef = useRef(null);
  const nextQuestionRequestRef = useRef("");
  const viewedQuestionRef = useRef("");

  useEffect(() => {
    routeStateRef.current = normalizedRouteState;
  }, [normalizedRouteState]);

  useEffect(() => {
    if (!isInitialized) {
      return undefined;
    }

    const unsubscribe = client.subscribe((nextState) => {
      setState(buildGamificationFlowState(nextState, routeStateRef.current));
    });

    if (isAuthenticated && authenticatedUserId) {
      client.connect(authenticatedUserId);
    } else {
      client.disconnect(false);
    }

    return () => {
      unsubscribe();
      if (!client.hasSubscribers()) {
        client.scheduleIdleDisconnect();
      }
    };
  }, [authenticatedUserId, client, isAuthenticated, isInitialized]);

  useEffect(() => {
    if (!normalizedRouteState || Object.keys(normalizedRouteState).length === 0) {
      return;
    }

    const routeSnapshot = JSON.stringify(normalizedRouteState);
    if (lastSyncedRouteStateRef.current === routeSnapshot) return;
    lastSyncedRouteStateRef.current = routeSnapshot;
    client.syncFlowState(normalizedRouteState);
  }, [client, normalizedRouteState]);

  useEffect(() => {
    persistGamificationFlowState(state);
  }, [state]);

  useEffect(() => {
    const questionId = Number(state?.currentQuestion?.question_id || 0);
    if (!questionId) {
      setQuestionTimeLeft(0);
      return;
    }

    handledAnswerRef.current = "";
    autoQuestionCompleteRef.current = "";
    nextQuestionRequestRef.current = "";
    answerScoreRefreshRef.current = "";
    setQuestionTimeTaken(0);
    const seconds = Number(state?.currentQuestion?.timer_seconds || 60);
    questionDeadlineRef.current = Date.now() + seconds * 1000;
    setQuestionTimeLeft(seconds);
  }, [state?.currentQuestion?.question_id, state?.currentQuestion?.timer_seconds, state?.questionReceivedAt]);

  useEffect(() => {
    const questionId = Number(state?.currentQuestion?.question_id || 0);
    if (!questionId || viewedQuestionRef.current === String(questionId)) return;
    viewedQuestionRef.current = String(questionId);
    storeQuestionBankQuestionTime({ question_id: questionId, timer_seconds: 3, is_view: 1 }).catch(() => {});
  }, [state?.currentQuestion?.question_id]);

  useEffect(() => {
    if (
      questionTimeLeft <= 0 ||
      !state?.currentQuestion?.question_id
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setQuestionTimeLeft(Math.max(0, Math.ceil((questionDeadlineRef.current - Date.now()) / 1000)));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [questionTimeLeft, state?.currentQuestion?.question_id, state?.liveStage]);

  useEffect(() => {
    if (!state?.currentQuestion?.question_id || state?.answerSubmitted || questionTimeLeft <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setQuestionTimeTaken((current) => Math.min(59, current + 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [state?.currentQuestion?.question_id, state?.answerSubmitted, questionTimeLeft]);

  useEffect(() => {
    if (state?.entryMode !== "random" || state?.liveStage !== "queue" || !state?.temp_room_id) {
      setWaitingCountdownLeft(0);
      waitingStartedRef.current = false;
      waitingDeadlineRef.current = 0;
      autoPingTriggeredRef.current = false;
      return;
    }
    if (waitingStartedRef.current) return;
    waitingStartedRef.current = true;
    const duration = Number(state?.waiting_time || 20);
    waitingDeadlineRef.current = Date.now() + duration * 1000;
    setWaitingCountdownLeft(duration);
  }, [state?.entryMode, state?.liveStage, state?.temp_room_id, state?.waiting_time]);

  useEffect(() => {
    if (!state?.start_confirmation_required || !state?.confirmation_started_at) {
      setStartCountdownLeft(0);
      return undefined;
    }

    const startedAt = Number(state.confirmation_started_at);
    const duration = Number(state.start_countdown_seconds || 10);
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((startedAt + duration * 1000 - Date.now()) / 1000));
      setStartCountdownLeft(remaining);
      if (remaining > 0 || confirmationResolvedRef.current === startedAt) return;
      confirmationResolvedRef.current = startedAt;
      const latest = client.state;
      if (!latest?.start_confirmed) {
        client.patch({ start_confirmation_required: false, liveStage: "match_failed", matchFailureMessage: "You did not join the battle in time." });
      } else if (!latest?.start_response_received || (latest?.users_list?.length || 0) < 2 || !latest?.battle_room_id) {
        client.patch({ start_confirmation_required: false, liveStage: "match_failed", matchFailureMessage: "No other players were found for this battle." });
      } else if (client.requestQuestion({ question_id: 0, question_index: 0, selected_lang: "EN" })) {
        client.patch({ start_confirmation_required: false, liveStage: "starting" });
      } else {
        client.patch({ start_confirmation_required: false, liveStage: "match_failed", matchFailureMessage: "Could not enter the battle. Please try again." });
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [client, state?.start_confirmation_required, state?.confirmation_started_at, state?.start_countdown_seconds]);

  useEffect(() => {
    if (waitingCountdownLeft <= 0 || state?.entryMode !== "random" || state?.liveStage !== "queue" || state?.start_confirmation_required) return undefined;
    const timer = window.setInterval(() => setWaitingCountdownLeft((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [waitingCountdownLeft, state?.entryMode, state?.liveStage, state?.start_confirmation_required]);

  useEffect(() => {
    const question = state?.currentQuestion;
    if (!question?.question_id || questionTimeLeft > 0 || Date.now() < questionDeadlineRef.current) return;
    const key = String(question.question_id);
    if (!state?.answerSubmitted && handledAnswerRef.current !== key) {
      handledAnswerRef.current = key;
      if (client.saveAnswer({
        question_id: question.question_id,
        user_answer_id: "",
        time_taken: Math.min(59, questionTimeTaken),
      })) client.battleScore();
    }
    if (Number(question.next_question_id || 0) > 0 && nextQuestionRequestRef.current !== key) {
      nextQuestionRequestRef.current = key;
      client.requestQuestion({
        question_id: question.next_question_id,
        question_index: question.question_index,
        selected_lang: question.next_question_available_langs?.[0] || "EN",
      });
    }
  }, [client, questionTimeLeft, questionTimeTaken, state?.currentQuestion, state?.answerSubmitted]);

  useEffect(() => {
    const response = state?.answerSaveResponse;
    if (!response) {
      return;
    }

    const responseKey = `${state?.question_id || 0}-${response?.is_all_user_answered ? 1 : 0}-${response?.next_question_id || 0}`;
    if (answerScoreRefreshRef.current !== responseKey) {
      answerScoreRefreshRef.current = responseKey;
      client.battleScore();
    }

    if (autoQuestionCompleteRef.current === responseKey) {
      return;
    }

    if (!response?.is_all_user_answered) {
      return;
    }

    autoQuestionCompleteRef.current = responseKey;

    if (Number(response?.next_question_id || state?.currentQuestion?.next_question_id || 0) > 0) {
      const questionKey = String(state?.question_id || 0);
      if (nextQuestionRequestRef.current === questionKey) return;
      nextQuestionRequestRef.current = questionKey;
      client.requestQuestion({
        question_id: Number(response?.next_question_id || state?.currentQuestion?.next_question_id || 0),
        question_index: Number(state?.currentQuestion?.question_index || 0),
        selected_lang: state?.currentQuestion?.next_question_available_langs?.[0] || "EN",
      });
      return;
    }

    client.completeGame();
  }, [
    client,
    state?.answerSaveResponse,
    state?.currentQuestion?.next_question_id,
    state?.currentQuestion?.question_index,
    state?.currentQuestion?.next_question_available_langs,
    state?.question_id,
  ]);

  useEffect(() => {
    if (state?.entryMode !== "random") {
      autoSearchTriggeredRef.current = false;
      return;
    }

    if (!state?.isSocketConnected) {
      return;
    }

    if (state?.temp_room_id || state?.battle_room_id) {
      return;
    }

    if (
      state?.liveStage &&
      !["", "queue", "left"].includes(state.liveStage)
    ) {
      return;
    }

    if (autoSearchTriggeredRef.current) {
      return;
    }

    const didSend = client.searchOpponent(client.state);
    autoSearchTriggeredRef.current = Boolean(didSend);
  }, [
    client,
    state?.entryMode,
    state?.isSocketConnected,
    state?.temp_room_id,
    state?.battle_room_id,
    state?.liveStage,
    state?.battles_id,
    state?.state_id,
    state?.subject_id,
  ]);

  const requiredPlayers = getRequiredPlayers(state);
  const joinedPlayers = Math.max(state?.users_list?.length || 0, state?.queue_users?.length || 0);
  const groupSearchEnded = Number(state?.battles_id) === 1 && waitingCountdownLeft === 0;

  useEffect(() => {
    if (state?.entryMode !== "random" || state?.liveStage !== "queue" ||
        state?.start_confirmation_required || !state?.temp_room_id || !waitingStartedRef.current) return undefined;

    const isSingle = Number(state?.battles_id) === 2;
    const isGroup = Number(state?.battles_id) === 1;
    const ready = requiredPlayers > 0 && joinedPlayers >= requiredPlayers;
    const searchEnded = groupSearchEnded && Date.now() + 1000 >= waitingDeadlineRef.current;
    if (isGroup && searchEnded && !ready) {
      client.patch({ liveStage: "match_failed", matchFailureMessage: "No opponents were found for this group battle." });
      return undefined;
    }
    if ((!isSingle && !isGroup) || !ready || (isGroup && !searchEnded)) return undefined;

    const startKey = `${state?.temp_room_id}-${state?.battles_id}`;
    if (autoRandomStartRef.current === startKey) return undefined;
    const openConfirmation = () => {
      const latest = client.state;
      if (latest?.entryMode !== "random" || latest?.liveStage !== "queue" ||
          Math.max(latest?.users_list?.length || 0, latest?.queue_users?.length || 0) < getRequiredPlayers(latest)) return;
      autoRandomStartRef.current = startKey;
      client.patch({
        start_confirmation_required: true,
        confirmation_started_at: Date.now(),
        start_countdown_seconds: Number(latest?.game_waiting_time) > 0 ? Number(latest.game_waiting_time) : 10,
        start_confirmed: false,
        start_response_received: false,
      });
    };
    if (isSingle) {
      autoRandomStartTimerRef.current = window.setTimeout(openConfirmation, 5000);
      return () => window.clearTimeout(autoRandomStartTimerRef.current);
    }
    openConfirmation();
    return undefined;
  }, [client, joinedPlayers, requiredPlayers, groupSearchEnded,
    state?.entryMode, state?.liveStage, state?.start_confirmation_required, state?.temp_room_id, state?.battles_id]);

  useEffect(() => {
    if (Number(state?.battles_id) !== 2 || state?.entryMode !== "random" || state?.liveStage !== "queue" ||
        state?.start_confirmation_required || !waitingStartedRef.current || waitingCountdownLeft !== 0 ||
        Date.now() < waitingDeadlineRef.current || joinedPlayers >= 2) return;
    client.patch({ liveStage: "match_failed", matchFailureMessage: "No opponent was found for this battle." });
  }, [client, joinedPlayers, waitingCountdownLeft, state?.battles_id, state?.entryMode,
    state?.liveStage, state?.start_confirmation_required]);

  useEffect(() => {
    if (
      state?.entryMode !== "random" ||
      state?.liveStage !== "queue" ||
      state?.start_confirmation_required ||
      waitingCountdownLeft > 4 ||
      waitingCountdownLeft <= 0 ||
      autoPingTriggeredRef.current ||
      !state?.temp_room_id ||
      Number(state?.battle_room_id || 0) > 0 ||
      joinedPlayers >= requiredPlayers
    ) {
      return;
    }

    autoPingTriggeredRef.current = true;
    client.matchPing(client.state);
  }, [
    client,
    joinedPlayers,
    requiredPlayers,
    state?.entryMode,
    state?.liveStage,
    state?.start_confirmation_required,
    state?.temp_room_id,
    state?.battle_room_id,
    state?.battles_id,
    state?.state_id,
    state?.subject_id,
    waitingCountdownLeft,
  ]);

  const submitAnswer = (optionId, isGuess = 0) => {
    const questionId = Number(state?.currentQuestion?.question_id || 0);
    if (!questionId || !state?.isSocketConnected || state?.answerSubmitted ||
        handledAnswerRef.current === String(questionId) || questionTimeLeft <= 0) return false;
    handledAnswerRef.current = String(questionId);
    const payload = {
      question_id: questionId,
      user_answer_id: Number(optionId),
      time_taken: Math.min(59, questionTimeTaken),
    };
    if (Number(isGuess) === 1) payload.is_guess = 1;
    const sent = client.saveAnswer(payload);
    if (sent) client.battleScore();
    if (!sent) handledAnswerRef.current = "";
    return sent;
  };

  const confirmStart = () => {
    if (state?.entryMode === "friend") {
      const latest = client.state;
      const players = latest?.users_list?.length || 0;
      const enoughPlayers = Number(latest?.battles_id) === 2 ? players === 2 : players > 2;
      if (!latest?.isRoomCreator || !latest?.battle_room_id || !enoughPlayers || latest?.friend_start_pending) return false;
      const requested = client.requestQuestion({ question_id: 0, question_index: 0, selected_lang: "EN" });
      if (requested) {
        client.patch({ friend_start_pending: true });
        if (Number(latest?.battles_id) === 2) client.startBattleGame(latest);
      }
      return requested;
    }
    if (!state?.start_confirmation_required || state?.start_confirmed || startCountdownLeft <= 0) return false;
    const sent = client.startGame(state);
    if (sent) client.patch({ start_confirmed: true, start_requested_at: Date.now() });
    return sent;
  };

  return {
    state,
    questionTimeLeft,
    questionTimeTaken,
    startCountdownLeft,
    waitingCountdownLeft,
    requiredPlayers,
    joinedPlayers,
    syncFlowState: (nextState) => client.syncFlowState(nextState),
    connect: () => {
      if (!isAuthenticated || !authenticatedUserId) return Promise.resolve(false);
      return client.connect(authenticatedUserId);
    },
    createRoom: () => client.createRoom(client.state),
    joinRoom: (room_code, options = {}) => client.joinRoom({ ...client.state, ...options, room_code }),
    searchOpponent: () => client.searchOpponent(state),
    confirmStart,
    cancelSearchConfirmation: () => {
      if (state?.start_confirmed) return client.leaveGame();
      return client.leaveQueueUser();
    },
    dismissMatchFailure: () => client.clearLiveIdentifiers(),
    requestQuestion: (payload) => client.requestQuestion(payload),
    submitAnswer,
    usePowerUp: (powerUpKey) => client.usePowerUp(powerUpKey),
    requestBattleScore: () => client.battleScore(),
    completeGame: () => client.completeGame(),
    leaveRoom: () => client.leaveRoom(),
    leaveQueueUser: () => client.leaveQueueUser(),
    leaveGame: () => client.leaveGame(),
    disconnect: (clearSession = false) => client.disconnect(clearSession),
    clearSession: () => client.clearSession(),
  };
}



